import React, { useMemo } from 'react';

import { Box } from '@rbilabs/components-library/build/components/layout';

import { IPicker } from '@rbi-ctg/menu';
import BonusPoints from 'components/bonus-points/bonus-points';
import { ReadMoreAccordion } from 'components/read-more-accordion';
import BlockRenderer from 'components/sanity-block-renderer';
import { useFeatureDisclaimers } from 'hooks/use-feature-disclaimers';
import { useFeatureMenu } from 'hooks/use-feature-menu';
import useMediaQuery, { MediaQuery } from 'hooks/use-media-query';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useProductWizardContext } from 'state/product-wizard';
import { hasExcessSodium } from 'utils/menu/sodium';
import { getSelectionsFromMenuData } from 'utils/wizard';

import { NutritionInformation, SodiumFlag } from '../nutrition-information';
import { PriceAndCalories } from '../price-and-calories';
import { createPickerPromotionMap } from '../utils';

import {
  BackgroundImageColor,
  ImageDisclaimerText,
  OverviewContainer,
  StyledProductPicture,
  Title,
} from './product-overview.styled';

const ProductOverview: React.FC = () => {
  const { menuObject, selectedProduct, userSelections } = useProductWizardContext();
  const { featureMenu } = useFeatureMenu();

  const isDesktop = useMediaQuery(MediaQuery.Desktop);
  const { featureDisclaimers } = useFeatureDisclaimers();

  const enableProductImageDisclaimer = useFlag(LaunchDarklyFlag.ENABLE_PRODUCT_IMAGE_DISCLAIMER);

  const { images: imagesDisclaimer } = featureDisclaimers || {};

  const promotionBonusPoints = useMemo(() => {
    if (!userSelections.pickerAspects) {
      return null;
    }
    const pickerPromotionMap = createPickerPromotionMap((menuObject as IPicker).options);
    let promotion;
    for (const pickerSelectionId in userSelections.pickerAspects) {
      const pickerIdentifier = userSelections.pickerAspects[pickerSelectionId];
      promotion = pickerPromotionMap[`${pickerSelectionId}-${pickerIdentifier}`];
      if (promotion) {
        break;
      }
    }
    return promotion?.bonusPoints;
  }, [menuObject, userSelections]);

  const isOverSodiumLimit = useMemo(() => {
    const { comboSlotSelections, modifierSelections } = getSelectionsFromMenuData({
      data: menuObject,
      pickerSelectionOverrides: userSelections.pickerAspects,
    });

    return hasExcessSodium({
      selectedOpt: selectedProduct,
      comboSlotSelections,
      modifierSelections,
    });
  }, [menuObject, selectedProduct, userSelections.pickerAspects]);

  const productImage =
    featureMenu?.enableDynamicPickerImages && selectedProduct?.image
      ? selectedProduct?.image
      : menuObject?.image;

  return (
    <OverviewContainer>
      <Box flexDirection="column" position="relative">
        <BackgroundImageColor />
        <StyledProductPicture image={productImage} alt="" />
        {enableProductImageDisclaimer && (
          <ImageDisclaimerText>{imagesDisclaimer?.locale}</ImageDisclaimerText>
        )}
      </Box>
      <Box
        padding="0 1rem 1rem"
        flexDirection="column"
        width={isDesktop ? '464px' : '100%'}
        margin="0 auto"
      >
        <Box alignContent="center" align="center" alignSelf="center">
          <Title data-testid="product-title">{selectedProduct?.name?.locale ?? ''}</Title>
          {isOverSodiumLimit && <SodiumFlag />}
        </Box>
        <PriceAndCalories />
        <ReadMoreAccordion>
          <BlockRenderer content={menuObject.description?.localeRaw} />
        </ReadMoreAccordion>
        {promotionBonusPoints && <BonusPoints bonusPoints={promotionBonusPoints} />}
        {isDesktop && <NutritionInformation />}
      </Box>
    </OverviewContainer>
  );
};

export default ProductOverview;
